/**
 * generateDevizHTML - Generează HTML-ul devizului din datele stocate
 * Folosit în portalul client pentru vizualizare/print/download
 */

const fmtNum = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export function generateDevizHTML(deviz) {
  if (!deviz) return "";

  const info = {
    beneficiar: deviz.beneficiar || "",
    beneficiarCui: deviz.beneficiarCui || "",
    executant: deviz.executant || "",
    executantCui: deviz.executantCui || "",
    obiectiv: deviz.obiectiv || "",
    adresa: deviz.adresa || "",
    stadiuFizic: deviz.stadiuFizic || "",
    numarDeviz: deviz.numarDeviz || deviz.numar_deviz || "",
    dataDeviz: deviz.dataDeviz || "",
    intocmitDe: deviz.intocmitDe || "",
    intocmitDeTel: deviz.intocmitDeTel || "",
    contractNumber: deviz.contractNumber || "",
    contractDate: deviz.contractDate || "",
  };

  const items = deviz.workItems || deviz.work_items || [];
  const totals = deviz.totals || {};
  const pct = deviz.percentages || {};

  const itemsHTML = items.map((item, idx) => {
    const matVal = (item.quantity || 0) * (item.materialPrice || 0);
    const manVal = (item.quantity || 0) * (item.manoperaPrice || 0);
    const total = matVal + manVal;
    return `<tr>
      <td class="text-center">${idx + 1}</td>
      <td class="text-left">${item.name || '-'}</td>
      <td class="text-center">${item.um || '-'}</td>
      <td class="text-right">${item.quantity || 0}</td>
      <td class="text-right">${fmtNum(item.materialPrice)}</td>
      <td class="text-right">${fmtNum(item.manoperaPrice)}</td>
      <td class="text-right">${fmtNum(matVal)}</td>
      <td class="text-right">${fmtNum(manVal)}</td>
      <td class="text-right">${fmtNum(total)}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html><html lang="ro"><head><meta charset="UTF-8">
<title>Deviz ${info.numarDeviz}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 10px; line-height: 1.4; color: #1a1a2e; margin: 0; padding: 15px; background: #fff; }
  .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #1a1a2e; }
  .header h1 { font-size: 18px; margin: 0 0 5px 0; color: #1a1a2e; text-transform: uppercase; letter-spacing: 2px; }
  .header .subtitle { font-size: 11px; color: #666; margin-top: 5px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; background: #f8f9fa; padding: 15px; border-radius: 8px; }
  .info-item { display: flex; }
  .info-label { font-weight: 600; color: #1a1a2e; min-width: 100px; }
  .info-value { color: #333; }
  .section-title { background: linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 100%); color: white; padding: 8px 15px; margin: 20px 0 10px 0; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 9px; }
  th { background: #e8e8f0; color: #1a1a2e; padding: 8px 5px; text-align: center; font-weight: 600; border: 1px solid #ccc; text-transform: uppercase; font-size: 8px; }
  td { padding: 6px 5px; border: 1px solid #ddd; vertical-align: middle; }
  .text-right { text-align: right; } .text-center { text-align: center; } .text-left { text-align: left; }
  .total-row { background: #e3f2fd; font-weight: 600; }
  .indent { padding-left: 25px !important; }
  .grand-total { background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%); color: white; font-size: 11px; }
  .grand-total td { border-color: #2e7d32; padding: 10px 5px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 20px; }
  .signature-box { width: 200px; text-align: center; }
  .signature-title { font-weight: 600; color: #1a1a2e; margin-bottom: 50px; font-size: 11px; }
  .signature-name { border-top: 1px solid #333; padding-top: 5px; font-size: 10px; color: #333; }
  .signature-line { font-size: 9px; color: #666; margin-top: 3px; }
  .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 8px; color: #888; text-align: center; }
</style></head><body>
  <div class="header">
    <h1>DEVIZ OFERTA</h1>
    <div class="subtitle">${info.numarDeviz} din ${info.dataDeviz}</div>
    ${info.contractNumber ? `<div class="subtitle" style="color:#2563eb; margin-top:3px;">Aferent contractului CTR ${info.contractNumber} din ${info.contractDate}</div>` : ''}
  </div>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Beneficiar:</span><span class="info-value">${info.beneficiar || '-'}${info.beneficiarCui ? ` (CUI: ${info.beneficiarCui})` : ''}</span></div>
    <div class="info-item"><span class="info-label">Obiectivul:</span><span class="info-value">${info.obiectiv || '-'}</span></div>
    <div class="info-item"><span class="info-label">Executant:</span><span class="info-value">${info.executant || '-'}${info.executantCui ? ` CUI: ${info.executantCui}` : ''}</span></div>
    <div class="info-item"><span class="info-label">Adresa:</span><span class="info-value">${info.adresa || '-'}</span></div>
    <div class="info-item"><span class="info-label">Intocmit de:</span><span class="info-value">${info.intocmitDe || '-'}${info.intocmitDeTel ? ` tel: ${info.intocmitDeTel}` : ''}</span></div>
    <div class="info-item"><span class="info-label">Stadiul fizic:</span><span class="info-value">${info.stadiuFizic || '-'}</span></div>
  </div>
  <div class="section-title">Sectiunea Tehnica - Capitole de Lucrari</div>
  <table><thead><tr>
    <th style="width:30px">Nr.</th><th>Capitol de lucrari / Descriere</th><th style="width:40px">U.M.</th>
    <th style="width:60px">Cantitate</th><th style="width:70px">Pret Material</th><th style="width:70px">Pret Manopera</th>
    <th style="width:80px">Val. Material</th><th style="width:80px">Val. Manopera</th><th style="width:80px">Total</th>
  </tr></thead><tbody>${itemsHTML}</tbody></table>
  <div class="section-title">Sectiunea Financiara - Recapitulatie Valorica</div>
  <table class="recap-table"><thead><tr>
    <th style="width:50%">Denumire</th><th style="width:16%">Material (RON)</th><th style="width:16%">Manopera (RON)</th><th style="width:18%">Total (RON)</th>
  </tr></thead><tbody>
    <tr class="total-row"><td>Cheltuieli directe</td><td class="text-right">${fmtNum(totals.totalMaterial)}</td><td class="text-right">${fmtNum(totals.totalManopera)}</td><td class="text-right">${fmtNum(totals.totalDirect)}</td></tr>
    <tr><td class="indent">+ Contributie munca (${pct.contributieMunca || 2.25}%)</td><td class="text-right">-</td><td class="text-right">${fmtNum(totals.contributieMunca)}</td><td class="text-right">${fmtNum(totals.contributieMunca)}</td></tr>
    <tr><td class="indent">+ Cheltuieli indirecte (${pct.cheltuieliIndirecte || 4}%)</td><td class="text-right">${fmtNum(totals.cheltuieliIndirecteMaterial)}</td><td class="text-right">${fmtNum(totals.cheltuieliIndirecteManopera)}</td><td class="text-right">${fmtNum((totals.cheltuieliIndirecteMaterial || 0) + (totals.cheltuieliIndirecteManopera || 0))}</td></tr>
    <tr><td class="indent">+ Profit (${pct.profit || 5}%)</td><td class="text-right">${fmtNum(totals.profitMaterial)}</td><td class="text-right">${fmtNum(totals.profitManopera)}</td><td class="text-right">${fmtNum((totals.profitMaterial || 0) + (totals.profitManopera || 0))}</td></tr>
    <tr class="total-row" style="background:#c8e6c9"><td colspan="3"><strong>TOTAL GENERAL (fara TVA)</strong></td><td class="text-right"><strong>${fmtNum(totals.totalFaraTVA)}</strong></td></tr>
    <tr><td colspan="3">TVA (${pct.tva || 21}%)</td><td class="text-right">${fmtNum(totals.tvaValue)}</td></tr>
    <tr class="grand-total"><td colspan="3"><strong>TOTAL GENERAL (inclusiv TVA)</strong></td><td class="text-right"><strong>${fmtNum(totals.totalCuTVA || totals.totalGeneral)} RON</strong></td></tr>
  </tbody></table>
  <div class="signatures">
    <div class="signature-box"><div class="signature-title">BENEFICIAR</div><div class="signature-name">${info.beneficiar || '____________________'}</div><div class="signature-line">Semnatura si stampila</div></div>
    <div class="signature-box"><div class="signature-title">EXECUTANT</div><div class="signature-name">${info.executant || '____________________'}</div><div class="signature-line">Semnatura si stampila</div></div>
  </div>
  <div class="footer"><p>Deviz "${info.numarDeviz}" generat cu <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a></p></div>
</body></html>`;
}
